<script setup>
import { reactive, ref, computed, onMounted } from 'vue'
import { useFormulario } from '@/composables/useFormulario'
import { useAuthStore } from '@/stores/auth'
import { useMetadataListasStore } from '@/stores/metadataListas'
import FormularioCadastro from '@/components/base/formulario/FormularioCadastro.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoSelect from '@/components/base/campos/CampoSelect.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import CampoBuscaLista from '@/components/base/campos/CampoBuscaLista.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import { parceiroSearch } from '@/search-definitions/parceiroSearch'
import { contaBancariaSearch } from '@/search-definitions/contaBancariaSearch'
import { FIN_TP_CONTA_BANC } from '@/constants/FIN_TP_CONTA_BANC'

const configBuscaInstituicaoFinanceira = computed(() => ({
  ...parceiroSearch,
  titulo: 'Buscar Instituição Financeira',
  filtrosFixos: {
    isInstFinanceira: true
  }
}))

const props = defineProps({
  itemParaEditar: {
    type: Object,
    default: null
  },
  modeloVazio: {
    type: Object,
    default: () => ({
      idContaBancaria: 0,
      descricao: '',
      lstTipoConta: '',
      idContaPrincipal: null,
      considerarFluxoCaixaDiario: null,
      idInstituicaoFinanceira: null,
      codigoSwiftBic: '',
      lstMoeda: '',
      lstStatus: '',
      numeroConta: '',
      agencia: '',
      lstTipoChavePix: '',
      chavePix: '',
      iban: '',
      lstRoutingNumberType: '',
      routingNumber: '',
      limiteChequeEspecial: null
    })
  }
})

const emit = defineEmits(['itemAtualizado', 'cancelar', 'show-notification'])

const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()

const mostrarAuditoria = ref(false)
const abrirAuditoria = () => { mostrarAuditoria.value = true }

const { item, isLoading, isEditing, entityInfo, submitForm, handleCancel } = useFormulario(props, emit)

const simNaoOptions = [
  { id: true, label: 'Sim' },
  { id: false, label: 'Não' }
]

const accordionState = reactive({
  capa: true,
  classificacao: false,
  instituicao: true,
  pix: false,
  internacional: false
})

const listas = reactive({})

const carregarListas = async () => {
  try {
    const result = await metadataStore.carregarListasParaTabelas(['ContaBancaria'])
    Object.assign(listas, result)
  } catch (e) {
    emit('show-notification', { message: 'Erro ao carregar listas auxiliares.', type: 'error' })
  }
}

// Configuração de busca para Conta Principal (self-reference, filtrada por tipo CORRENTE)
const configBuscaContaPrincipal = computed(() => {
  if (!item.value.lstTipoConta) return null
  const config = contaBancariaSearch(FIN_TP_CONTA_BANC.Corrente)
  return {
    ...config,
    titulo: 'Selecionar Conta Corrente Principal',
    filtrosFixos: {
      ...config.filtrosFixos,
      idEmpresa: authStore.empresaAtiva ? Number(authStore.empresaAtiva) : null
    }
  }
})

// Só exibe o campo ContaPrincipal quando o tipo é INVEST_AUTOMATICO
const mostrarContaPrincipal = computed(() => {
  return item.value.lstTipoConta === FIN_TP_CONTA_BANC.InvestimentoAutomatico
})

async function salvar () {
  const payload = {
    ...item.value,
    idInstituicaoFinanceira: item.value.idInstituicaoFinanceira ? Number(item.value.idInstituicaoFinanceira) : null,
    idContaPrincipal: item.value.idContaPrincipal ? Number(item.value.idContaPrincipal) : null,
    considerarFluxoCaixaDiario: item.value.considerarFluxoCaixaDiario || false,
    limiteChequeEspecial: item.value.limiteChequeEspecial || 0
  }

  await submitForm('ContasBancarias', 'idContaBancaria', payload)
}

onMounted(() => {
  carregarListas()
})
</script>

<template>
  <FormularioCadastro
    module-name="Conta Bancária"
    :is-loading="isLoading"
    :is-editing="isEditing"
    :entity-info="entityInfo"
    :accordion-state="accordionState"
    @submit="salvar"
    @cancelar="handleCancel"
  >
    <template #acoes-header v-if="isEditing">
      <button type="button" class="btn-icon-minimalista me-3" @click="abrirAuditoria" title="Ver Histórico de Alterações">
        <i class="bi bi-clock-history"></i>
      </button>
    </template>

    <!-- FRAME 1: DADOS GERAIS -->
    <div class="card mb-4">
      <div class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center" @click="accordionState.capa = !accordionState.capa">
        <h5 class="mb-0"><i class="bi bi-file-earmark-text me-2 text-primary"></i> Dados Gerais</h5>
        <i :class="['bi', accordionState.capa ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
      </div>
      <div class="card-body" v-show="accordionState.capa">
        <div class="row g-3">
          <div class="col-md-8">
            <CampoInput
              id="descricao"
              label="Descrição"
              v-model="item.descricao"
              placeholder="Ex: Conta Principal - Corrente BRL"
              :error="errors?.descricao"
              required
            />
          </div>
          <div class="col-md-4">
            <CampoBuscaLista
              id="lstStatus"
              label="Status"
              v-model="item.lstStatus"
              tabela="ContaBancaria"
              :error="errors?.lstStatus"
              required
            />
          </div>
        </div>
        <div class="row g-3 mt-2">
          <div class="col-md-4">
            <CampoBuscaLista
              id="lstTipoConta"
              label="Tipo de Conta"
              v-model="item.lstTipoConta"
              tabela="ContaBancaria"
              :error="errors?.lstTipoConta"
              required
            />
          </div>
          <div class="col-md-3">
            <CampoBuscaLista
              id="lstMoeda"
              label="Moeda"
              v-model="item.lstMoeda"
              tabela="ContaBancaria"
              :error="errors?.lstMoeda"
              required
            />
          </div>
          <div class="col-md-2">
            <CampoSelect
              id="considerarFluxoCaixaDiario"
              label="Fluxo Caixa Diário?"
              v-model="item.considerarFluxoCaixaDiario"
              :options="simNaoOptions"
              option-label="label"
              option-value="id"
              descricao-informativa="Compoe a liquidez imediata?"
            />
          </div>
          <div class="col-md-3">
            <CampoInput
              id="limiteChequeEspecial"
              label="Limite Cheque Especial"
              type="moeda"
              v-model="item.limiteChequeEspecial"
              placeholder="0,00"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- FRAME 2: CLASSIFICAÇÃO E COMPORTAMENTO -->
    <div class="card mb-4">
      <div class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center" @click="accordionState.classificacao = !accordionState.classificacao">
        <h5 class="mb-0"><i class="bi bi-diagram-3 me-2 text-primary"></i> Classificação e Comportamento</h5>
        <i :class="['bi', accordionState.classificacao ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
      </div>
      <div class="card-body" v-show="accordionState.classificacao">
        <div class="row g-3">
          <div class="col-md-6" v-if="mostrarContaPrincipal">
            <CampoBusca
              id="idContaPrincipal"
              label="Conta Corrente Principal"
              v-model="item.idContaPrincipal"
              :configuracao-busca="configBuscaContaPrincipal"
              @show-notification="(p) => emit('show-notification', p)"
            />
          </div>
          <div class="col-md-6" v-if="!mostrarContaPrincipal">
            <div class="alert alert-info border-0 rounded-3 mb-0 d-flex align-items-center">
              <i class="bi bi-info-circle-fill me-2 text-primary"></i>
              <small>O vínculo com conta principal aplica-se apenas para contas do tipo <strong>Investimento Automático</strong>.</small>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- FRAME 3: DADOS DA INSTITUIÇÃO -->
    <div class="card mb-4">
      <div class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center" @click="accordionState.instituicao = !accordionState.instituicao">
        <h5 class="mb-0"><i class="bi bi-building me-2 text-primary"></i> Dados da Instituição</h5>
        <i :class="['bi', accordionState.instituicao ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
      </div>
      <div class="card-body" v-show="accordionState.instituicao">
        <div class="row g-3">
          <div class="col-md-5">
            <CampoBusca
              id="idInstituicaoFinanceira"
              label="Instituição Financeira (Banco)"
              v-model="item.idInstituicaoFinanceira"
              :configuracao-busca="configBuscaInstituicaoFinanceira"
              :error="errors?.idInstituicaoFinanceira"
            />
          </div>
          <div class="col-md-3">
            <CampoInput
              id="codigoSwiftBic"
              label="Código Swift / BIC"
              v-model="item.codigoSwiftBic"
              placeholder="Ex: ITAUBRSP"
              descricao-informativa="Código internacional do banco para transferências"
            />
          </div>
          <div class="col-md-2">
            <CampoInput
              id="agencia"
              label="Agência"
              v-model="item.agencia"
              placeholder="Ex: 0001"
            />
          </div>
          <div class="col-md-2">
            <CampoInput
              id="numeroConta"
              label="Número da Conta"
              v-model="item.numeroConta"
              placeholder="Ex: 12345-6"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- FRAME 4: CHAVE PIX -->
    <div class="card mb-4">
      <div class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center" @click="accordionState.pix = !accordionState.pix">
        <h5 class="mb-0"><i class="bi bi-qr-code me-2 text-primary"></i> Chave PIX</h5>
        <i :class="['bi', accordionState.pix ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
      </div>
      <div class="card-body" v-show="accordionState.pix">
        <div class="row g-3">
          <div class="col-md-4">
            <CampoSelect
              id="lstTipoChavePix"
              label="Tipo de Chave PIX"
              v-model="item.lstTipoChavePix"
              :options="listas.LstTipoChavePix || []"
              option-label="textoLista"
              option-value="idListaSistemaItem"
            />
          </div>
          <div class="col-md-8">
            <CampoInput
              id="chavePix"
              label="Chave PIX"
              v-model="item.chavePix"
              placeholder="CPF, CNPJ, E-mail, Telefone ou Chave Aleatória"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- FRAME 5: TRANSFERÊNCIAS INTERNACIONAIS -->
    <div class="card mb-4">
      <div class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center" @click="accordionState.internacional = !accordionState.internacional">
        <h5 class="mb-0"><i class="bi bi-globe2 me-2 text-primary"></i> Transferências Internacionais</h5>
        <i :class="['bi', accordionState.internacional ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
      </div>
      <div class="card-body" v-show="accordionState.internacional">
        <div class="row g-3">
          <div class="col-md-4">
            <CampoInput
              id="iban"
              label="IBAN"
              v-model="item.iban"
              placeholder="Ex: BR9700360305000010009795493P1"
            />
          </div>
          <div class="col-md-4">
            <CampoSelect
              id="lstRoutingNumberType"
              label="Tipo Routing Number"
              v-model="item.lstRoutingNumberType"
              :options="listas.LstRoutingNumberType || []"
              option-label="textoLista"
              option-value="idListaSistemaItem"
            />
          </div>
          <div class="col-md-4">
            <CampoInput
              id="routingNumber"
              label="Routing Number"
              v-model="item.routingNumber"
              placeholder="Ex: ABA 021000021"
            />
          </div>
        </div>
      </div>
    </div>

  </FormularioCadastro>

  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="ContaBancaria"
    :chave-primaria="item.idContaBancaria"
    :titulo-registro="item.descricao"
    contexto="tenant"
    :dicionarios="{
      'lstStatus': listas.LstStatus,
      'lstMoeda': listas.LstMoeda,
      'lstTipoChavePix': listas.LstTipoChavePix,
      'lstRoutingNumberType': listas.LstRoutingNumberType,
      'lstTipoConta': listas.LstTipoConta
    }"
    @fechar="mostrarAuditoria = false"
  />
</template>
